import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Alert, Badge, PageHeader, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { type ArticleSummary, formatDate, type KbStatus, useKnowledgeForm } from './data';

const STATUSES: KbStatus[] = ['publicado', 'borrador', 'archivado'];

/**
 * Base de Conocimientos (guía 7.8): buscar una guía, filtrar por categoría y, para IT, ver los borradores y lo
 * archivado. Si no aparece nada, el siguiente paso es abrir un ticket.
 */
export function KnowledgePage() {
  const form = useKnowledgeForm();
  const [params, setParams] = useSearchParams();
  const search = params.get('buscar') ?? '';
  const category = params.get('categoria') ?? '';
  const status = (STATUSES as string[]).includes(params.get('estado') ?? '') ? (params.get('estado') as KbStatus) : 'publicado';
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);

  const query = new URLSearchParams();
  if (search) query.set('buscar', search);
  if (category) query.set('categoria', category);
  if (status !== 'publicado') query.set('estado', status);
  const articles = useQuery({
    queryKey: ['conocimiento', 'lista', search, category, status],
    queryFn: () => api<ArticleSummary[]>(`/api/conocimiento?${query}`),
    placeholderData: (prev) => prev,
  });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    update({ buscar: draft.trim() || null });
  };
  const can = form.data?.can;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('kb.titulo')}
        description={t('kb.descripcion')}
        actions={
          can?.publish && (
            <Link to="/conocimiento/nuevo" className="rounded bg-marca px-4 py-2 text-sm font-medium text-white hover:bg-marca-claro">
              {t('kb.nuevo')}
            </Link>
          )
        }
      />

      <form onSubmit={submit} role="search" className="flex max-w-2xl gap-2">
        <label htmlFor="kb-buscar" className="sr-only">
          {t('kb.buscar')}
        </label>
        <input
          id="kb-buscar"
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('kb.buscar_ayuda')}
          className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-4 py-2.5 text-sm placeholder:text-slate-400 focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/25"
        />
        <button type="submit" className="rounded bg-marca px-5 py-2.5 text-sm font-medium text-white hover:bg-marca-claro focus:outline-none focus-visible:ring-2 focus-visible:ring-marca/40">
          {t('kb.buscar')}
        </button>
      </form>

      {can?.seeInternal && (
        <div role="tablist" aria-label={t('kb.estado')} className="flex gap-1 border-b border-slate-200">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={s === status}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${s === status ? 'border-marca font-medium text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              onClick={() => update({ estado: s === 'publicado' ? null : s })}
            >
              {t(`kb.estado_${s}_plural` as MessageKey)}
            </button>
          ))}
        </div>
      )}

      {form.data && form.data.categories.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('kb.categorias')}>
          <CategoryChip active={!category} onClick={() => update({ categoria: null })}>
            {t('kb.todas')}
          </CategoryChip>
          {form.data.categories.map((c) => (
            <CategoryChip key={c.id} active={category === c.id} onClick={() => update({ categoria: category === c.id ? null : c.id })} title={c.description ?? undefined}>
              {c.name}
            </CategoryChip>
          ))}
        </div>
      )}

      {!articles.data ? (
        articles.error ? <Alert>{errorMessage(articles.error)}</Alert> : <Spinner label={t('comun.cargando')} />
      ) : articles.data.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 px-5 py-6 text-sm">
          <p className="text-slate-700">{search ? t('kb.sin_resultados', { texto: search }) : t('kb.vacio')}</p>
          {status === 'publicado' && (
            <p className="mt-2 text-slate-600">
              {t('kb.no_encuentra')}{' '}
              <Link to="/tickets/nuevo" className="font-medium text-marca hover:underline">
                {t('kb.abrir_ticket')}
              </Link>
              {search && (
                <>
                  {' · '}
                  <Link to={`/tickets?${new URLSearchParams({ buscar: search })}`} className="text-marca hover:underline">
                    {t('kb.buscar_en_tickets')}
                  </Link>
                </>
              )}
            </p>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white" aria-label={t('kb.resultados')}>
          {articles.data.map((a) => (
            <li key={a.id} className="px-5 py-4">
              <Link to={`/conocimiento/${a.code}`} className="text-base font-medium text-slate-900 hover:text-marca hover:underline">
                {a.title}
              </Link>
              {a.summary && <p className="mt-0.5 max-w-[75ch] text-sm text-slate-600">{a.summary}</p>}
              <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                {a.audience === 'it' && <Badge tone="amber">{t('kb.solo_it')}</Badge>}
                {a.category && <span>{a.category}</span>}
                {a.app && <span>{a.app}</span>}
                <span>{t('kb.actualizado', { fecha: formatDate(a.updatedAt) })}</span>
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CategoryChip({ active, onClick, title, children }: { active: boolean; onClick: () => void; title?: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-marca/40 ${
        active ? 'border-marca bg-marca text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
      }`}
    >
      {children}
    </button>
  );
}
